import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Lock, RefreshCw, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const PLATFORM_COLOR = {
  fanvue: "text-emerald-400",
  telegram: "text-sky-400",
  instagram: "text-rose-400",
  x: "text-white",
};

export default function Fans() {
  const { t } = useT();
  const [fans, setFans] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dossiering, setDossiering] = useState(false);

  const load = () => api.get("/fans").then((r) => setFans(r.data));
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/connections/fanvue/sync");
      toast.success(`Sync: ${r.data.fans ?? 0} fans, ${r.data.messages ?? 0} messages`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const importHistory = async () => {
    if (!window.confirm(
      "Stiahne sa čo najviac histórie zo VŠETKÝCH Fanvue chatov (až 40 strán × 50 správ na chat).\n\n" +
      "Môže to trvať niekoľko minút. Nechaj okno otvorené. Pokračovať?"
    )) return;
    setImporting(true);
    try {
      const r = await api.post("/connections/fanvue/import-history", { max_pages: 40 });
      toast.success(`Import hotový: ${r.data.messages} nových správ z ${r.data.fans} chatov`);
      load();
    } catch (e) {
      toast.error("Import zlyhal — pozri logy dash-fanvue");
    } finally { setImporting(false); }
  };

  const buildDossiers = async () => {
    if (!window.confirm(
      "AI prečíta históriu každého fanúšika a vytvorí z nej dossier (fakty, štýl, preferencie).\n\n" +
      "Stojí to kredity (~0,03 kr na fanúšika). Fanúšikovia, čo už dossier majú, sa preskočia. Pokračovať?"
    )) return;
    setDossiering(true);
    try {
      const r = await api.post("/ai/regenerate-notes-all", { limit: 60 });
      toast.success(`Dossiery: ${r.data.done} vytvorených, ${r.data.skipped} preskočených (${r.data.cost_kr} kr)`);
      load();
    } catch (e) {
      toast.error("Generovanie zlyhalo");
    } finally { setDossiering(false); }
  };

  const removeFan = async (fan) => {
    if (!window.confirm(`Delete ${fan.name}? This removes the chat history in dash (not on Fanvue).`)) return;
    try {
      await api.delete(`/fans/${fan.id}`);
      setFans((p) => p.filter((f) => f.id !== fan.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = (filter === "all" ? fans : fans.filter((f) => f.platform === filter))
    .filter((f) => !search || (f.name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-8 lg:p-12 fade-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-white/40">{t("fans")}</div>
          <h1 className="font-serif text-5xl mt-2">{t("fans")}</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex items-center gap-2 border border-white/10 px-3 py-1.5">
            <Search size={12} className="text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="search…"
              data-testid="fans-search"
              className="bg-transparent text-xs outline-none w-32 placeholder:text-white/30" />
          </div>
          <button onClick={syncNow} disabled={syncing} data-testid="fans-sync"
            className="flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/10 disabled:opacity-50">
            <RefreshCw size={11} className={syncing ? "animate-spin" : ""} /> {syncing ? "syncing…" : "refresh"}
          </button>
          <button onClick={importHistory} disabled={importing} data-testid="fans-import"
            title="Stiahne čo najviac histórie zo všetkých Fanvue chatov"
            className="flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/20 text-white/70 hover:bg-white/5 disabled:opacity-50">
            📚 {importing ? "importujem…" : "import histórie"}
          </button>
          <button onClick={buildDossiers} disabled={dossiering} data-testid="fans-dossiers"
            title="AI vytvorí dossier z histórie pre všetkých fanúšikov"
            className="flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
            🧠 {dossiering ? "generujem…" : "dossiery pre všetkých"}
          </button>
          {["all", "fanvue", "telegram"].map((p) => (
            <button
              key={p}
              onClick={() => setFilter(p)}
              data-testid={`filter-${p}`}
              className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border ${
                filter === p ? "border-white bg-white/5 text-white" : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-white/5">
        <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5 bg-white/[0.02]">
          <div className="col-span-1">platform</div>
          <div className="col-span-4">name</div>
          <div className="col-span-2 text-right">spend</div>
          <div className="col-span-2 text-right">tips</div>
          <div className="col-span-2">nsfw</div>
          <div className="col-span-1 text-right">action</div>
        </div>
        {filtered.length === 0 && <div className="px-5 py-10 text-center text-white/30 text-sm">{t("no_fans_yet")}</div>}
        {filtered.map((f) => (
          <div
            key={f.id}
            className="grid grid-cols-12 px-5 py-3.5 items-center border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            data-testid={`fan-row-${f.id}`}
          >
            <div className={`col-span-1 text-[10px] uppercase tracking-widest ${PLATFORM_COLOR[f.platform]}`}>
              {f.platform.slice(0, 3)}
            </div>
            <div className="col-span-4 font-serif text-lg truncate">{f.name}</div>
            <div className="col-span-2 text-right font-serif text-lg tabular-nums text-emerald-400">
              ${(f.spend_total || 0).toFixed(2)}
            </div>
            <div className="col-span-2 text-right font-mono text-xs text-white/60">
              ${(f.tips_total || 0).toFixed(2)}
            </div>
            <div className="col-span-2">
              {f.nsfw_unlocked ? (
                <span className="text-[10px] uppercase tracking-widest text-rose-400 flex items-center gap-1">
                  <Lock size={10} className="-rotate-12" /> unlocked
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-white/30">sfw</span>
              )}
            </div>
            <div className="col-span-1 text-right flex items-center justify-end gap-3">
              <button onClick={() => removeFan(f)} data-testid={`btn-del-fan-${f.id}`}
                className="text-white/30 hover:text-rose-400 transition-colors" title="Delete fan">
                <Trash2 size={13} />
              </button>
              <Link
                to={`/inbox?fan=${f.id}`}
                className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white"
                data-testid={`btn-open-chat-${f.id}`}
              >
                chat →
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
